// Types mirroring the response of `POST /api/v2/search/` (see
// `backend_django/origin/search_engine/views.py` and
// `search_engine/search.py::_group_by_entity`). The backend groups
// chunk-level hits up into entity-level rows before returning, so each
// item below is one chat/task/note that matched, not one chunk.

export type ChatTypeLabel = "dm" | "gm" | "mdm" | "pm";
export type NoteTypeLabel = "personal" | "task" | "chat";
// "project" only appears in agent-emitted citation chips (the search
// backend never returns project rows). It links to the project's
// task-list view; no project-preview modal exists.
// "spotlight_answer" is a collected past Spotlight answer (the team-shared
// answer-reuse lane). It surfaces in typeahead and carries `answer_text` +
// `answer_sources` so the UI can render the past answer with clickable
// source chips. See backend `chunkers/spotlight_answer_chunker.py`.
// "milestone" carries the backing task's `task_id` + `project_id`, so it
// deep-links through the same task view the app uses to open milestones.
export type EntityType =
    | "chat"
    | "task"
    | "milestone"
    | "note"
    // A note folder (MIP-841): My Notes, Team Notes or cross-team shared.
    | "note_folder"
    | "project"
    | "todo"
    | "spotlight_answer";

export interface SpotlightResult {
    entity_type: EntityType;
    entity_id: string; // e.g. "dm:5", "pm:1:thread:3", "task:123", "note:personal:42"
    title: string | null;
    snippet: string | null;
    score: number;
    keyword_rank: number | null;
    vector_rank: number | null;
    matched_chunk_types: string[]; // e.g. ["chat_message", "chat_thread_window"]
    // Analyzer-aware tokens (incl. stemmed/synonym forms) extracted from
    // the OpenSearch highlight response. Empty for vector-only hits.
    // The frontend merges these with the literal query tokens when
    // bolding matches.
    matched_terms: string[];
    updated_at: string | null;

    // Chat-specific (present when entity_type === "chat")
    chat_type: ChatTypeLabel | null;
    chat_id: string | null;
    thread_id: string | null;
    // The specific message inside the chat / thread that matched.
    // Null when the matching chunk wasn't a single message (e.g. a
    // thread-window chunk or an anchor chunk), or for non-chat results.
    // Spotlight uses it to deep-link the chat URL down to the bubble.
    message_id: string | null;

    // Task-specific
    task_id: string | null;
    // Human-readable task identifier ("<project.code>-<project_task_number>",
    // e.g. "PRJ-42"). Always shown to end users in place of the raw
    // task_id. Null on legacy rows or when the task lacks a project.
    task_display_id: string | null;
    // Where a note lives, outermost first: the folder chain, or
    // project › milestone › task, or the chat's name (MIP-839). Present
    // only when the viewer may see those names (the server decides).
    context_path?: string[] | null;
    // `note_folder` rows: the folder to open, and its sidebar space.
    folder_id?: string | null;
    folder_scope?: "personal" | "team" | null;
    // Task status name ("Open" / "WIP" / "Blocked" / "Pending" /
    // "Closed"), projected onto task and milestone rows so the result
    // row can render the status chip. Optional on purpose — it is
    // absent against older backends, and MILESTONE rows only carry it
    // once their chunks have been reingested (the field was added to
    // `milestone_chunker` after the index was first built). Treat a
    // missing value as "unknown" and render no chip.
    task_status?: string | null;

    // Note-specific
    note_id: string | null;
    note_type: NoteTypeLabel | null;

    // Cross-cutting
    project_id: string | null;
    related_entity_ids: string[];

    // spotlight_answer lane only (a collected past answer). `answer_text` is
    // the stored answer body with inline `[type:id]` citation tokens;
    // `answer_sources` are the SpotlightResult-shaped sources the answer cited.
    // Both absent on every other entity type.
    answer_text?: string | null;
    answer_sources?: SpotlightResult[];

    // True on sources the agent CREATED / UPDATED via an approved write
    // tool this run (create_task, create_note, update_todo_item, …).
    // The chip row always surfaces these, even when the answer's prose
    // fails to cite them — the user must always get a clickable ref to
    // what the agent changed (see `citedChipSources`). Absent on read /
    // search sources and on older backends.
    operated?: boolean;
}

export interface SearchResponse {
    query: string;
    results: SpotlightResult[];
}

export interface SearchRequest {
    query: string;
    team_id: string;
    entity_types?: EntityType[];
    // Hard project scope: only content whose `project_id` is in this
    // list comes back. Because it's a filter and not a boost, anything
    // with no project at all (DMs/GMs, personal notes, todos) drops out
    // — that's the intended reading of "only these projects". Omitted
    // entirely when the user has selected no projects.
    project_ids?: string[];
    date_from?: string;
    date_to?: string;
    limit?: number;
    use_vector?: boolean;
    // Relevance-threshold overrides (the search view's sanctioned
    // per-call knobs — see `search_engine/views.py`). The Spotlight
    // typeahead passes an explicit, much lower absolute floor than the
    // backend default so the *relative* floor (0.5 × top score) governs
    // instead. The relative floor adapts per query and returns every
    // result within ~half the top hit's confidence, whereas the default
    // absolute floor (calibrated for the agent path) sits above almost
    // every real typeahead RRF score and collapsed results to a fixed 3.
    // `min_score_ratio`: relative floor as a fraction of the top score.
    // `min_score`: absolute RRF floor; 0 disables it.
    min_score_ratio?: number;
    min_score?: number;
}

// Chat-type label (backend) → integer code expected by
// `useCM.moveToSpecificChat`. Mirrors CHAT_TYPE constants in
// `backend_django/origin/views/chat/*_views.py`.
export const CHAT_TYPE_CODE: Record<ChatTypeLabel, number> = {
    dm: 1,
    gm: 2,
    pm: 3,
    mdm: 4,
};
